import { ReactNode, useContext, useState, createContext, useEffect, useRef } from 'react';

import {
  useCreateUserSettingsMutation,
  useGetUserSettingsQuery,
  CreateUserSettingsMutation,
  useUpdateUserSettingsMutation,
} from './service/user-settings.service';

export enum Season {
  SPRING = 'SPRING',
  SUMMER = 'SUMMER',
  AUTUMN = 'AUTUMN',
  WINTER = 'WINTER',
}

export const ALL_EDITORIAL_CONTENT = 'ALL';

export type EditorialContentFilters = {
  [season in Season]: string;
};

interface UserPreferences {
  feedbackPanelAlwaysCollapsed: boolean | null;
  automaticSearchEnabled: boolean;
  season: Season | null;
  editorialContentFilters: EditorialContentFilters;
}

interface UserPreferencesContextModel extends UserPreferences {
  saveUserPreferences: (newPreferences: Partial<UserPreferences>) => void | null;
  isLoading: boolean;
}

const settingName = 'my-stihl.preferences';
const userPreferencesInitialState: UserPreferences = {
  feedbackPanelAlwaysCollapsed: null,
  automaticSearchEnabled: false,
  season: null,
  editorialContentFilters: {
    [Season.SPRING]: ALL_EDITORIAL_CONTENT,
    [Season.SUMMER]: ALL_EDITORIAL_CONTENT,
    [Season.AUTUMN]: ALL_EDITORIAL_CONTENT,
    [Season.WINTER]: ALL_EDITORIAL_CONTENT,
  },
};

export const UserPreferencesContext = createContext<UserPreferencesContextModel>({
  ...userPreferencesInitialState,
  saveUserPreferences: () => null,
  isLoading: false,
});

export const UserPreferencesContextProvider = ({ children }: { children: ReactNode }) => {
  const alreadyCreatedRef = useRef(false);
  const [userPreferences, setUserPreferences] = useState<UserPreferences>(userPreferencesInitialState);

  const saveUserPreferences = (newPreferences: Partial<UserPreferences>) => setUserPreferences((prev) => ({ ...prev, ...newPreferences }));

  const [{ data, fetching }] = useGetUserSettingsQuery(settingName);
  const [_c, create] = useCreateUserSettingsMutation();
  const [_u, update] = useUpdateUserSettingsMutation();

  useEffect(() => {
    if (data?.userSetting?.setting) {
      saveUserPreferences(data.userSetting.setting as UserPreferences);
      alreadyCreatedRef.current = !!data?.userSetting?.setting;
    }
  }, [data]);

  useEffect(() => {
    if (userPreferences) {
      (alreadyCreatedRef.current ? update : create)(settingName, userPreferences).then((response) => {
        if ((response?.data as CreateUserSettingsMutation)?.createUserSetting?.name === settingName) {
          alreadyCreatedRef.current = true;
        }
      });
    }
  }, [userPreferences]);

  return (
    <UserPreferencesContext.Provider value={{ ...userPreferences, saveUserPreferences, isLoading: fetching }}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

export const useUserPreferencesContext = () => {
  const context = useContext(UserPreferencesContext);

  if (!context) {
    throw new Error('useUserPreferencesContext must be used in a component wrapped with UserPreferencesContextProvider');
  }

  return context;
};
